import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { all_routes } from "../../../Router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Table from "../../../core/pagination/datatable";
import { setToogleHeader } from "../../../core/redux/action";
import { api_get, api_post } from "../../../helpers/index";

import { ChevronUp, Edit, Eye, PlusCircle, RotateCcw, Trash2 } from "feather-icons-react/build/IconComponents";

const GroupTierList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");

  const handleRetrieveList = async (keyword = "") => {
    const result = await api_get(`spinwheel/group_tier/list/1?keyword=${keyword}`);

    if (result.status) {
      const totalPages = result.data.pagination.total_page;
      let allData = result.data.collection;

      for (let page = 2; page <= totalPages; page++) {
        const pageResult = await api_get(`spinwheel/group_tier/list/${page}?keyword=${keyword}`);
        if (pageResult.status) {
          allData = allData.concat(pageResult.data.collection);
        }
      }

      if (allData.length > 0) {
        const dataWithKeys = allData.map((item) => ({
          ...item,
          id: item.group_tier_id,
        }));

        setDataSource(dataWithKeys);
      }
    } else {
      setDataSource([]);
    }
  };

  useEffect(() => {
    handleRetrieveList();
  }, []);

  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleRetrieveList(searchKeyword);
  };

  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const route = all_routes;

  const columns = [
    {
      title: "Group Tier ID",
      dataIndex: "group_tier_id",
      render: (text, record) => (
        <span className="group-tier-id">
          <Link to={`/group-tier-details?group_tier_id=${record.group_tier_id}`}>{text}</Link>
        </span>
      ),
      sorter: (a, b) => a.group_tier_id.localeCompare(b.group_tier_id),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Tier IDs",
      dataIndex: "tier_id",
      render: (tier_ids) => <span>{tier_ids.join(", ")}</span>,
    },
    {
      title: "Last Updated At",
      dataIndex: "last_updated_at",
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => new Date(a.last_updated_at) - new Date(b.last_updated_at),
    },
    {
      title: "Last Updated By",
      dataIndex: "last_updated_by",
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.last_updated_by.localeCompare(b.last_updated_by),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" to={`/group-tier-details?group_tier_id=${record.group_tier_id}`}>
              <Eye className="feather-view" />
            </Link>
            <Link className="me-2 p-2" to={`/edit-group-tier?group_tier_id=${record.group_tier_id}`}>
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              onClick={() => showConfirmationAlert(record.group_tier_id)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = (group_tier_id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const deleteResult = await api_post(`group_tier/delete/${group_tier_id}`);
        if (deleteResult.status) {
          MySwal.fire("Deleted!", "The group tier has been deleted.", "success");
          handleRetrieveList(searchKeyword);
        } else {
          MySwal.fire("Error!", "There was an error deleting the group tier.", "error");
        }
      }
    });
  };

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Group Tier List</h4>
              <h6>Manage your group tiers</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link data-bs-toggle="tooltip" onClick={() => handleRetrieveList(searchKeyword)}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            <Link to={route.addGroupTier} className="btn btn-added">
              <PlusCircle className="me-2 iconsize" />
              Add New Group Tier
            </Link>
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <form onSubmit={handleSearchSubmit} className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                    value={searchKeyword}
                    onChange={handleSearch}
                  />
                  <button type="submit" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </button>
                </form>
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={dataSource} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupTierList; 